import { z } from 'zod';

/**
 * Environment variable schema with Zod validation
 * Ensures all required environment variables are present and valid
 */
const envSchema = z.object({
  // API Configuration
  apiUrl: z.string().url('VITE_API_URL must be a valid URL').default('http://localhost:3001'),

  // Environment
  env: z.enum(['development', 'production', 'test']).default('development'),

  // Feature Flags
  enableAnalytics: z.boolean().default(false),
  enableErrorReporting: z.boolean().default(false),

  // App Configuration
  appName: z.string().default('FinLab'),
  appVersion: z.string().default('0.1.0'),

  // Optional: Add more environment variables as needed
  // For example:
  // sentryDsn: z.string().url().optional(),
  // googleAnalyticsId: z.string().optional(),
});

/**
 * Parse and validate environment variables
 * Throws an error if validation fails
 */
function parseEnv() {
  const rawEnv = {
    apiUrl: import.meta.env.VITE_API_URL,
    env: import.meta.env.VITE_ENV || import.meta.env.MODE,
    enableAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
    enableErrorReporting: import.meta.env.VITE_ENABLE_ERROR_REPORTING === 'true',
    appName: import.meta.env.VITE_APP_NAME,
    appVersion: import.meta.env.VITE_APP_VERSION,
  };

  try {
    return envSchema.parse(rawEnv);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const missingVars = error.errors.map((err) => {
        const path = err.path.join('.');
        return `${path}: ${err.message}`;
      });

      console.error('❌ Invalid environment variables:', missingVars);
      throw new Error(
        `Environment validation failed:\n${missingVars.join('\n')}`
      );
    }
    throw error;
  }
}

/**
 * Type-safe environment variables
 * Access like: env.apiUrl, env.enableAnalytics, etc.
 */
export const env = parseEnv();

/**
 * Type for environment variables (useful for testing/mocking)
 */
export type Env = z.infer<typeof envSchema>;

/**
 * Check if running in development mode
 */
export const isDevelopment = env.env === 'development';

/**
 * Check if running in production mode
 */
export const isProduction = env.env === 'production';

/**
 * Check if running in test mode
 */
export const isTest = env.env === 'test';

/**
 * Get the API URL with proper path handling
 */
export function getApiUrl(path: string = ''): string {
  const baseUrl = env.apiUrl.replace(/\/$/, ''); // Remove trailing slash
  const cleanPath = path.replace(/^\//, ''); // Remove leading slash
  return cleanPath ? `${baseUrl}/${cleanPath}` : baseUrl;
}

/**
 * Validate that all required environment variables are set
 * Useful for startup checks
 */
export function validateEnvironment(): boolean {
  try {
    parseEnv();
    return true;
  } catch {
    return false;
  }
}

/**
 * Print environment configuration (for debugging)
 * Note: Be careful not to log sensitive information in production
 */
export function printEnvironmentInfo(): void {
  if (!isDevelopment) {
    return;
  }

  console.group('🌍 Environment Configuration');
  console.table({
    'App Name': env.appName,
    'App Version': env.appVersion,
    'Environment': env.env,
    'API URL': env.apiUrl,
    'Analytics Enabled': env.enableAnalytics,
    'Error Reporting': env.enableErrorReporting,
  });
  console.groupEnd();
}
